import { memo } from 'react';
import PropTypes from 'prop-types';
import { Badge, IconHighlight } from '@aimsos/ui';

import PickerOption from './PickerOption.jsx';

import { Boxes } from 'lucide-react';

/**
 * One entity card (CT-5191 / CT-5192).
 *
 * Shows what the design source of truth shows: name, a count beside it, a
 * one-line description, the integration, and the governance tags.
 *
 * The count is `metaLabel` rather than a number, because what there is to
 * count belongs to the caller: the catalog behind this picker lists composites
 * and counts the tables each one joins, not columns.
 *
 * The integration is always visible because the catalogue can hold same-named
 * entities from different sources; the tags are visible because a widget built
 * on an ungoverned or PII source inherits that, and the picker is the moment to
 * say so rather than after the widget is saved.
 *
 * `memo()`: these render in a grid of eight, and the parent passes one stable
 * `onSelect`.
 */
const EntityPickerCard = memo(function EntityPickerCard({
  entity,
  integrationLabel,
  metaLabel,
  ungovernedLabel,
  piiLabel,
  selected,
  onSelect,
}) {
  return (
    <PickerOption
      value={entity.id}
      selected={selected}
      onSelect={onSelect}
      ariaLabel={`${entity.name} · ${integrationLabel}`}
      className="p-3"
    >
      <div className="flex min-w-0 items-start gap-2.5" aria-hidden="true">
        <IconHighlight icon={Boxes} bgColor="Primary" iconColor="PrimaryAlt" size="small" />

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 items-baseline gap-1.5">
            <span className="truncate text-sm font-medium text-content">{entity.name}</span>
            <span className="shrink-0 text-xs text-content-muted">{`· ${metaLabel}`}</span>
          </div>

          {entity.description ? (
            <p className="m-0 line-clamp-2 text-xs text-content-muted">{entity.description}</p>
          ) : null}

          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <Badge size="small" variant="secondary">{integrationLabel}</Badge>
            {entity.governed ? null : (
              <Badge size="small" variant="warning">{ungovernedLabel}</Badge>
            )}
            {entity.hasPii ? <Badge size="small" variant="danger">{piiLabel}</Badge> : null}
          </div>
        </div>
      </div>
    </PickerOption>
  );
});

EntityPickerCard.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    integration: PropTypes.string,
    governed: PropTypes.bool,
    hasPii: PropTypes.bool,
  }).isRequired,
  integrationLabel: PropTypes.string.isRequired,
  metaLabel: PropTypes.string.isRequired,
  ungovernedLabel: PropTypes.string.isRequired,
  piiLabel: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

export default EntityPickerCard;
